// src/App.js
// Updated: October 13, 2025
// Includes GA4 Route Tracking for https://app.whydiditdie.com

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { supabase } from './lib/supabase';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'react-hot-toast';

// Import page components
import Dashboard from './components/Dashboard';
import CampaignList from './pages/CampaignList';
import CampaignDeepAnalysis from './components/CampaignDeepAnalysis';
import CampaignDetective from './components/CampaignDetective';
import WasteHunter from './pages/WasteHunter';
import WasteHunterAI from './pages/WasteHunterAI';
import DecisionTracker from './pages/DecisionTracker';
import CompetitorWatch from './pages/ContentEngine';
import MarketIntelligenceEngine from './pages/MarketIntelligenceEngine';
import PlatformIntelligence from './pages/PlatformIntelligence';
import SubscriptionDashboard from './pages/SubscriptionDashboard';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Homepage from './pages/Homepage';
import PaymentCallback from './pages/PaymentCallback';
import PricingPage from './pages/PricingPage';
import FeaturesPage from './pages/FeaturesPage';
import HowItWorksPage from './pages/HowItWorks';
import IntegrationsPage from './pages/IntegrationsPage';
import AboutPage from './pages/AboutPage';
import BlogPage from './pages/BlogPage';
import CareersPage from './pages/CareersPage';
import ContactPage from './pages/ContactPage';
import HelpCenterPage from './pages/HelpCenterPage';
import DocumentationPage from './pages/DocumentationPage';
import StatusPage from './pages/StatusPage';
import CommunityPage from './pages/CommunityPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import SecurityPage from './pages/SecurityPage';
import ContactSalesPage from './pages/ContactSales';
import PerformanceGoals from './pages/PerformanceGoals';
import BlogPostPage from './pages/BlogPostPage';
import SubscribePage from './pages/SubscribePage';
import AdminDashboard from './pages/AdminDashboard';
import logger from './utils/logger';

// ✅ GA4 Route Tracker Hook
const MEASUREMENT_ID = 'G-3051QY9MPD';

function GA4RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    if (!window.gtag) return;

    const path = `${location.pathname}${location.search}${location.hash}`;
    window.gtag('config', MEASUREMENT_ID, {
      page_path: path,
    });

    // Optional: send an explicit page_view event for Realtime reporting
    window.gtag('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: path,
    });
  }, [location]);

  return null;
}

// 🔒 Protected Route
const ProtectedRoute = ({ children }) => {
  const { user: authUser, loading: authLoading, isAuthenticated } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!authLoading) {
          if (authUser && isAuthenticated) {
            setUser(authUser);
            setLoading(false);
            return;
          }
          if (!authUser) {
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();
            if (sessionError) {
              setError(sessionError.message);
              setUser(null);
            } else if (session?.user) {
              setUser(session.user);
            } else {
              setUser(null);
            }
          }
        }
      } catch (err) {
        setError(err.message);
        setUser(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      switch (event) {
        case 'SIGNED_IN':
          if (session?.user) setUser(session.user);
          setError(null);
          break;
        case 'SIGNED_OUT':
          setUser(null);
          setError(null);
          break;
        case 'TOKEN_REFRESHED':
        case 'USER_UPDATED':
          if (session?.user) setUser(session.user);
          break;
        default:
          break;
      }
      setLoading(false);
    });

    return () => {
      mounted = false;
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  }, [authUser, authLoading, isAuthenticated]);

  if (loading || authLoading) {
    return (
      <>
        <Toaster position="top-right" />
        <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white text-center p-8">
          <div className="text-5xl mb-4 animate-spin">🔄</div>
          <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-emerald-400 to-blue-500 bg-clip-text text-transparent">
            Checking Authentication...
          </h2>
          <p className="text-gray-400 text-base">Please wait while we verify your session</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Toaster position="top-right" />
        <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white text-center p-8">
          <div className="text-5xl mb-4 text-red-500">⚠️</div>
          <h2 className="text-2xl font-bold mb-2 text-red-500">Authentication Error</h2>
          <p className="text-red-300 text-base mb-6 max-w-lg">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white py-3 px-6 rounded-lg text-base font-bold cursor-pointer transition-all duration-200 transform hover:scale-105"
          >
            🔄 Retry
          </button>
        </div>
      </>
    );
  }

  if (!authUser && !user) return <Navigate to="/login" replace />;

  return (
    <>
      <Toaster position="top-right" />
      {children}
    </>
  );
};

// 🔐 OAuth Callback
const OAuthCallback = () => {
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkSession = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 1000));
        const { data, error } = await supabase.auth.getSession();
        if (error || !data?.session?.user) {
          window.location.href = '/login';
        } else {
          window.location.href = '/dashboard';
        }
      } catch (err) {
        logger.error('OAuth callback error:', err);
        window.location.href = '/login';
      } finally {
        setChecking(false);
      }
    };
    checkSession();
  }, []);

  if (checking) {
    return <div className="flex items-center justify-center min-h-screen bg-black text-white">Completing sign in...</div>;
  }

  return null;
};

// 🚀 App Entry
function App() {
  const [appLoading, setAppLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (mounted) setAppLoading(false);
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      if (mounted) setAppLoading(false);
    });

    return () => {
      mounted = false;
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  }, []);

  if (appLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white text-center p-8">
        <div className="text-5xl mb-4 animate-bounce">🚀</div>
        <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-emerald-400 to-blue-500 bg-clip-text text-transparent">
          Loading WhyDidItDie...
        </h2>
        <p className="text-gray-400 text-base">Initializing your marketing analytics platform</p>
      </div>
    );
  }

  return (
    <Router>
      <AuthProvider>
        <GA4RouteTracker />
        <div className="App min-h-screen bg-gray-950 text-white">
          <main className="main-content">
            <Routes>
              <Route path="/" element={<Homepage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/auth/callback" element={<OAuthCallback />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/how-it-works" element={<HowItWorksPage />} />
              <Route path="/contact-sales" element={<ContactSalesPage />} />
              <Route path="/integrations" element={<IntegrationsPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/help" element={<HelpCenterPage />} />
              <Route path="/docs" element={<DocumentationPage />} />
              <Route path="/status" element={<StatusPage />} />
              <Route path="/community" element={<CommunityPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/payment/callback" element={<PaymentCallback />} />
              <Route path="/subscription" element={<ProtectedRoute><SubscriptionDashboard /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/campaigns" element={<ProtectedRoute><CampaignList /></ProtectedRoute>} />
              <Route path="/campaign-detective" element={<ProtectedRoute><CampaignDetective /></ProtectedRoute>} />
              <Route path="/campaign-analysis/:campaignId" element={<ProtectedRoute><CampaignDeepAnalysis /></ProtectedRoute>} />
              <Route path="/timeline/:campaignId" element={<ProtectedRoute><CampaignDeepAnalysis /></ProtectedRoute>} />
              <Route path="/waste-hunter" element={<ProtectedRoute><WasteHunterAI /></ProtectedRoute>} />
              <Route path="/waste-hunter-ai" element={<ProtectedRoute><WasteHunterAI /></ProtectedRoute>} />
              <Route path="/waste-hunter-legacy" element={<ProtectedRoute><WasteHunter /></ProtectedRoute>} />
              <Route path="/decision-tracker" element={<ProtectedRoute><DecisionTracker /></ProtectedRoute>} />
              <Route path="/decision/:decisionId" element={<ProtectedRoute><DecisionTracker /></ProtectedRoute>} />
              <Route path="/campaign/:campaignId/decisions" element={<ProtectedRoute><DecisionTracker /></ProtectedRoute>} />
              <Route path="/campaign/:campaignId/log-decision" element={<ProtectedRoute><DecisionTracker /></ProtectedRoute>} />
              <Route path="/content-engine" element={<ProtectedRoute><CompetitorWatch /></ProtectedRoute>} />
              <Route path="/competitor-watch/facebook" element={<ProtectedRoute><CompetitorWatch /></ProtectedRoute>} />
              <Route path="/competitor-watch/google" element={<ProtectedRoute><CompetitorWatch /></ProtectedRoute>} />
              <Route path="/competitor-watch/tiktok" element={<ProtectedRoute><CompetitorWatch /></ProtectedRoute>} />
              <Route path="/competitor-watch/linkedin" element={<ProtectedRoute><CompetitorWatch /></ProtectedRoute>} />
              <Route path="/competitor-watch/history" element={<ProtectedRoute><CompetitorWatch /></ProtectedRoute>} />
              <Route path="/competitor-watch/analysis/:analysisId" element={<ProtectedRoute><CompetitorWatch /></ProtectedRoute>} />
              <Route path="/campaign/:campaignId/competitor-analysis" element={<ProtectedRoute><CompetitorWatch /></ProtectedRoute>} />
              <Route path="/competitor-watch/alerts" element={<ProtectedRoute><CompetitorWatch /></ProtectedRoute>} />
              <Route path="/market-intelligence-engine" element={<ProtectedRoute><MarketIntelligenceEngine /></ProtectedRoute>} />
              <Route path="/platform-intelligence" element={<ProtectedRoute><PlatformIntelligence /></ProtectedRoute>} />
              <Route path="/analytics/decisions" element={<ProtectedRoute><DecisionTracker /></ProtectedRoute>} />
              <Route path="/analytics/team-performance" element={<ProtectedRoute><DecisionTracker /></ProtectedRoute>} />
              <Route path="/analytics/competitor-insights" element={<ProtectedRoute><CompetitorWatch /></ProtectedRoute>} />
              <Route path="/analytics/differentiation-report" element={<ProtectedRoute><CompetitorWatch /></ProtectedRoute>} />
              <Route path="/analytics/waste-hunter" element={<ProtectedRoute><WasteHunterAI /></ProtectedRoute>} />
              <Route path="/settings/performance-goals" element={<ProtectedRoute><PerformanceGoals /></ProtectedRoute>} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/subscribe" element={<SubscribePage />} />
              <Route path="/admin" element={<AdminDashboard />} />
            </Routes>
          </main>
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
